import { useLayoutEffect, useRef, useState } from 'react';
import { ChartFrame, formatCount } from '@helios/ui/charts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@helios/ui';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import type { FinanceCommandCenterPayload } from './financeCommandCenter.types';
import { formatRecordedAmount } from './financeInvoices.types';
import { companyProfitSteps, recordedDecimal as decimal } from './financePerformance.view';
import { companyMetricScope } from './financeOverview.view';

type Accounting = FinanceCommandCenterPayload['accounting'];
export function FinanceCompanyProfitBridge({
  data,
  canRead,
  onSourceReport,
}: {
  data: Accounting;
  canRead: boolean;
  onSourceReport?: () => void;
}) {
  const { identity } = useSessionScopeFence();
  const scope = JSON.stringify(sessionScopeKey(identity));
  const [selection, setSelection] = useState<{ id: string; scope: string } | null>(null);
  const steps = companyProfitSteps(data);
  const source = companyMetricScope(data, 'revenue');
  const hasPlot = canRead && steps.length > 0;
  const plot = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 760, height: 225 });
  useLayoutEffect(() => {
    const target = plot.current;
    if (!target) return;
    const measure = () => {
      if (!target.clientWidth || !target.clientHeight) return;
      const next = { width: target.clientWidth, height: target.clientHeight };
      setSize((previous) =>
        previous.width === next.width && previous.height === next.height ? previous : next,
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasPlot]);
  const selected =
    selection?.scope === scope && canRead ? steps.find((row) => row.id === selection.id) : null;
  const maximum = Math.max(
    0,
    ...steps.flatMap((row) => [Number(row.start) / 100, Number(row.end) / 100]),
  );
  const minimum = Math.min(
    0,
    ...steps.flatMap((row) => [Number(row.start) / 100, Number(row.end) / 100]),
  );
  const rawStep = (maximum - minimum || 1) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step =
    ([1, 1.5, 2, 2.5, 5, 10].find((value) => value >= rawStep / magnitude) ?? 10) * magnitude;
  const lower = Math.floor(minimum / step) * step;
  const upper = Math.ceil(maximum / step) * step || step;
  const ticks = Array.from(
    { length: Math.round((upper - lower) / step) + 1 },
    (_, index) => lower + index * step,
  );
  const left = Math.max(
    52,
    Math.min(88, formatCount(Math.max(Math.abs(lower), Math.abs(upper))).length * 6.3 + 12),
  );
  const right = size.width - 15;
  const top = 22;
  const bottom = Math.max(top + 60, size.height - 40);
  const yAmount = (value: number) =>
    bottom - ((value - lower) / (upper - lower || 1)) * (bottom - top);
  const y = (value: bigint) => yAmount(Number(value) / 100);
  const column = (right - left) / (steps.length || 1);
  const barWidth = column * 0.64;
  const x = (index: number) => left + column * index + (column - barWidth) / 2;
  const open = (id: string) => setSelection({ id, scope });
  return (
    <>
      <ChartFrame
        title="Where company profit goes"
        basis={`${source.basis || 'Basis unreported'} · ${source.dateLabel} · ${source.currency || 'Currency unreported'} · last imported company P&L.`}
        description="Solid totals are imported. Connecting effects are exact differences between those totals; they do not invent expense categories."
        state={!canRead ? 'unavailable' : !data || !steps.length ? 'unavailable' : 'ok'}
        unavailableMessage={
          !canRead
            ? 'Company accounting reports are restricted by your current permissions.'
            : 'Imported revenue, gross profit and net income are required for this bridge.'
        }
        tableColumns={['Measure', 'Recorded amount', 'Evidence basis']}
        tableRows={steps.map((row) => ({
          id: row.id,
          cells: [row.label, formatRecordedAmount(decimal(row.amount)), row.formula],
        }))}
        onDrill={open}
        height={225}
      >
        <div ref={plot} className="h-full min-h-0 w-full">
          <svg
            viewBox={`0 0 ${size.width} ${size.height}`}
            className="h-full w-full"
            role="img"
            aria-label="Company profit bridge from imported report totals"
          >
            <g aria-hidden="true">
              {ticks.map((tick) => (
                <g key={tick}>
                  <line
                    x1={left}
                    x2={right}
                    y1={yAmount(tick)}
                    y2={yAmount(tick)}
                    stroke="hsl(var(--border))"
                    strokeDasharray={tick === 0 ? undefined : '2 4'}
                    opacity={tick === 0 ? 1 : 0.65}
                  />
                  <text
                    x={left - 10}
                    y={yAmount(tick) + 3}
                    textAnchor="end"
                    fill="hsl(var(--muted-foreground))"
                    fontSize="10"
                  >
                    {formatCount(tick)}
                  </text>
                </g>
              ))}
              <line x1={left} x2={left} y1={top} y2={bottom} stroke="hsl(var(--border))" />
              {steps.slice(0, -1).map((row, index) => (
                <line
                  key={row.id}
                  data-waterfall-connector={row.id}
                  x1={x(index) + barWidth}
                  x2={x(index + 1)}
                  y1={y(row.end)}
                  y2={y(row.end)}
                  stroke="hsl(var(--brand-gold) / .7)"
                  strokeDasharray="4 3"
                />
              ))}
            </g>
            {steps.map((row, index) => {
              const barX = x(index),
                barTop = Math.min(y(row.start), y(row.end)),
                height = Math.abs(y(row.start) - y(row.end));
              return (
                <g
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Inspect ${row.label}`}
                  onClick={() => open(row.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      open(row.id);
                    }
                  }}
                  className="finance-profit-bridge__mark cursor-pointer focus:outline-none"
                >
                  <rect
                    x={barX}
                    y={barTop}
                    width={barWidth}
                    height={Math.max(height, 1)}
                    rx="2"
                    fill={
                      index % 2
                        ? 'hsl(var(--muted-foreground) / .55)'
                        : 'hsl(var(--brand-gold) / .82)'
                    }
                  />
                  <text
                    x={barX + barWidth / 2}
                    y={Math.max(12, barTop - 8)}
                    textAnchor="middle"
                    fill="currentColor"
                    fontSize={Math.max(8, Math.min(11, column / 11))}
                    fontWeight="600"
                  >
                    {formatRecordedAmount(decimal(row.amount))}
                  </text>
                  <text
                    x={barX + barWidth / 2}
                    y={size.height - 15}
                    textAnchor="middle"
                    fill="currentColor"
                    fontSize={Math.max(8, Math.min(10, column / 10))}
                  >
                    {row.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </ChartFrame>
      <Dialog
        open={Boolean(selected)}
        onOpenChange={(openValue) => {
          if (!openValue) setSelection(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selected?.label} · company report evidence</DialogTitle>
            <DialogDescription>
              Imported company report and explicit arithmetic, separate from project WIP and
              invoices.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xl font-semibold tabular-nums">
            {selected ? formatRecordedAmount(decimal(selected.amount)) : '—'}{' '}
            {source.currency || 'currency unreported'}
          </p>
          <p className="text-muted-foreground text-sm">{selected?.formula}</p>
          <p className="text-muted-foreground text-xs">
            Source: {data?.source} · {source.basis || 'Basis unreported'} · {source.dateLabel}. Last
            completed import:{' '}
            {data?.lastSyncedAt ? new Date(data.lastSyncedAt).toLocaleString() : 'Unavailable'}.
            Transaction-level report lineage is not stored in this aggregate.
          </p>
          {onSourceReport ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelection(null);
                onSourceReport();
              }}
            >
              Inspect underlying company report
            </Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => setSelection(null)}>
            Close company report evidence
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
